import { useCallback, useEffect, useRef, useState } from 'react';
import { QueueTable, useQueueGrid } from '@components/QueueTable';
import { StatusTabs, type TabDef, type TabKey } from '@components/StatusTabs';
import { EventDrawer } from '@components/EventDrawer';
import { approveBatch, reviewEvent } from '@lib/care-api';
import { useEventQueue } from '@lib/useEventQueue';
import { STOPS_FILTER_LABEL, stopsOnly } from '@lib/stop-pending';
import { statusLabel, type EventStatus } from '@lib/care-model';
import { STAGED_WORDING, batchSettledSentence, pendingCountOf } from '@lib/staged-presses';
import { useLeaveGuard } from '@lib/leave-guard';

/**
 * FinalReview - the QA lane (rev4). Two tabs: Awaiting review (in_review, the drawer's Send to
 * review lands here) and Cleared. The await tab supports checkbox multi-select + "Approve
 * (N)" (POST review/approve-batch: per-event resolution emit + the dated station archive, batchId
 * reported), and per-row expand opens the drawer in reviewMode with the inline Approve (+ resolution
 * note) / Return to queue controls. Ungated today (any signed-in agent approves, incl. own items);
 * a future approver gate is one check server-side (William ruling #10).
 */

/**
 * CONSOLE FAULT 3 (27 Oct 2026): what the banner says when an Approve request is cut. A 500 with no body is the
 * platform's 45-second limit, not a refusal: the cases it reached are approved, the list reloads, and the rest wait.
 */
export function approveCutWords(status: number, error?: string): string {
  if (status === 500 && !error) {
    return 'The server stopped before answering (its 45-second limit). The cases it reached are approved and the list is reloaded; press Approve again for those still waiting.';
  }
  return `Approve failed (HTTP ${status}${error ? `: ${error}` : ''}).`;
}

const REVIEW_TABS: TabDef[] = [
  { key: 'in_review', label: 'Awaiting review' },
  { key: 'cleared', label: 'Cleared' },
];

export function FinalReview() {
  const [tab, setTab] = useState<TabKey>('in_review');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [batchNote, setBatchNote] = useState('');
  const [banner, setBanner] = useState('');
  const [approving, setApproving] = useState(false);
  // Console fault 2 (27 Oct 2026): while an Approve runs, leaving the page asks first.
  useLeaveGuard(approving);

  const { data, loading, reload } = useEventQueue({
    status: tab === 'all' ? undefined : tab,
    q,
  });
  // PART N (William, 17 Oct 2026 1:02 AM: "We wil make sure each agent confirms the STOP and they will do this
  // each am"): the Stops filter lists only the cases with a stop waiting, oldest first, with its count.
  const [stopsView, setStopsView] = useState(false);
  const stopRows = stopsOnly(data?.items ?? []);

  const awaiting = tab === 'in_review';

  const toggleCheck = useCallback((id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // William 2026-07-08: every column sorts asc/desc. Default matches the server order (date desc).
  // Row 356: through the shared grid (Shift-click adds a 2nd and 3rd key; slicers; remembered).
  // `items` is what is ON SCREEN, sorted and sliced: Select all, Approve and Archive act on it alone.
  const grid = useQueueGrid({
    table: stopsView ? 'final-review-stops' : 'final-review',
    items: stopsView ? stopRows : data?.items ?? [],
    defaultSort: [{ k: 'date', asc: stopsView }],
    slicers: ['channel', 'source', 'campaign', 'actions', 'match', 'handledBy'],
    selectable: awaiting,
    checkedIds: checked,
    onToggleCheck: toggleCheck,
    showActions: true,
  });
  const items = grid.rows;

  // A ticked row a slicer then hides is UNTICKED: an approval must never reach a row she cannot see.
  useEffect(() => {
    setChecked((prev) => {
      if (prev.size === 0) return prev;
      const shown = new Set(items.map((r) => r.eventId));
      const kept = [...prev].filter((id) => shown.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [items]);

  const toggleAll = () => {
    setChecked((prev) => (prev.size === items.length ? new Set<string>() : new Set(items.map((i) => i.eventId))));
  };

  // Wave 3 item 4: the presses queued on the TICKED rows on screen (the list row's pendingCount): how
  // many, on how many cases. Zero on every row while the deployment does not stage presses.
  const tickedQueued = items.reduce(
    (acc, r) => {
      const n = checked.has(r.eventId) ? pendingCountOf(r) : 0;
      return n > 0 ? { presses: acc.presses + n, cases: acc.cases + 1 } : acc;
    },
    { presses: 0, cases: 0 },
  );

  const runApproveSelected = async () => {
    const ids = items.map((i) => i.eventId).filter((id) => checked.has(id));
    if (!ids.length) return;
    setApproving(true);
    setBanner(`Approving ${ids.length} event${ids.length > 1 ? 's' : ''}...`);
    try {
      // CONSOLE FAULT 3 (27 Oct 2026): the server starts no new case past its time limit and answers the rest as
      // notStarted; they are sent again, round after round, until none is left or a round makes no progress. Each
      // round is its own batch (its own archive bundle); the banner adds them up.
      const note = batchNote.trim() || undefined;
      const approved: string[] = [];
      const failed: { id: string; error: string }[] = [];
      const stampFailed: { id: string; error: string }[] = [];
      const batchIds: string[] = [];
      const pending: unknown[] = [];
      let left = ids;
      let cut = '';
      for (;;) {
        const r = await approveBatch(left, note);
        if (!r.ok) {
          cut = approveCutWords(r.status, (r.data as { error?: string }).error);
          break;
        }
        approved.push(...((r.data.approved as string[] | undefined) ?? []));
        failed.push(...((r.data.failed as { id: string; error: string }[] | undefined) ?? []));
        stampFailed.push(...((r.data.stampFailed as { id: string; error: string }[] | undefined) ?? []));
        if (r.data.batchId) batchIds.push(String(r.data.batchId));
        pending.push(...((r.data.pending as unknown[] | undefined) ?? []));
        const rest = (r.data.notStarted as string[] | undefined) ?? [];
        if (!rest.length || rest.length >= left.length) break;
        left = rest;
        setBanner(`Approving: ${approved.length} of ${ids.length} done, ${rest.length} to go...`);
      }
      // Wave 3 item 4: what these approvals did with the presses queued on the cases (applied, or
      // refused by name with the reason), and the cases a queued Close file closed with no
      // resolution. '' when the answer lists none, which is every batch with nothing queued.
      const pressed = batchSettledSentence(pending.length ? { pending } : {});
      const notReached = ids.length - approved.length - failed.length;
      setBanner(
        (cut && !approved.length && !failed.length ? cut : `Approved ${approved.length} of ${ids.length}.`) +
          (batchIds.length ? ` Batch: ${batchIds.join(', ')} (station archive under review-batches/).` : '') +
          (failed.length ? ` Failed: ${failed.map((f) => `${f.id} (${f.error})`).join(', ')}.` : '') +
          (stampFailed.length
            ? ` Batch-stamp issues (resolutions already shipped): ${stampFailed.map((f) => `${f.id} (${f.error})`).join(', ')}.`
            : '') +
          (pressed ? ` ${pressed}` : '') +
          (cut && (approved.length || failed.length) ? ` ${cut}` : '') +
          (!cut && notReached > 0 ? ` ${notReached} not reached: press Approve again for those still waiting.` : ''),
      );
      if (!cut && notReached <= 0) {
        setChecked(new Set());
        setBatchNote('');
      }
    } finally {
      setApproving(false);
      void reload();
    }
  };

  /**
   * Row 217(a) (William, 16 Sep 2026): force an archive from the to-be-approved queue. Each checked
   * item is ended WITHOUT a resolution (status closed-no-action, "Handled - no action" on the
   * Archive page, reopenable), one call per event through the same review route as approve. The
   * note is REQUIRED: it is the only record of why nothing was sent. Nothing reaches the Portal.
   */
  const [archiving, setArchiving] = useState(false);
  const runArchiveSelected = async () => {
    const ids = items.map((i) => i.eventId).filter((id) => checked.has(id));
    if (!ids.length) return;
    const note = batchNote.trim();
    if (!note) {
      setBanner('Archiving without a resolution needs a note: it is the only record of why nothing was sent.');
      return;
    }
    if (!window.confirm(`Archive ${ids.length} event${ids.length > 1 ? 's' : ''} WITHOUT a resolution?

No resolution will be sent to the Portal. Each becomes "Handled - no action" and can be reopened.

Note: ${note}`)) return;
    setArchiving(true);
    setBanner(`Archiving ${ids.length} event${ids.length > 1 ? 's' : ''}...`);
    const done: string[] = [];
    const failed: { id: string; error: string }[] = [];
    try {
      for (const id of ids) {
        const r = await reviewEvent(id, 'archive', note);
        if (r.ok) done.push(id);
        else failed.push({ id, error: String((r.data as { error?: string }).error ?? `HTTP ${r.status}`) });
      }
      setBanner(
        `Archived ${done.length} of ${ids.length} without a resolution.` +
          (failed.length ? ` Failed: ${failed.map((f) => `${f.id} (${f.error})`).join(', ')}.` : ''),
      );
      if (done.length) { setChecked(new Set()); setBatchNote(''); }
    } finally {
      setArchiving(false);
      void reload();
    }
  };

  /** View follows the work: an inline drawer Approve moves the selected event in_review -> cleared,
   * so switch to the Cleared tab to keep the row visible. Return to queue goes to in_progress
   * (not a review tab), so the guard leaves the tab alone - the event left the review lane.
   * Latest-value refs: the drawer's mutate captures this callback at click time, and the mutation
   * round-trip can land AFTER the operator switched tabs or events - the guards must read the
   * CURRENT tab/selected, not the click-time closure, or a late response yanks the view (and
   * clears the checked set). */
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const onDrawerChanged = (updated?: { eventId: string; status: string }) => {
    void reload();
    if (!updated || updated.eventId !== selectedRef.current) return;
    const curTab = tabRef.current;
    if (curTab === 'all' || updated.status === curTab) return;
    if (!REVIEW_TABS.some((t) => t.key === updated.status)) return;
    setTab(updated.status as TabKey);
    setChecked(new Set());
    setBanner(`Moved to ${statusLabel(updated.status as EventStatus)} - tab switched to follow it.`);
  };

  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">Member Care · QA</div>
        <h1 className="section-title">
          Final <em>review</em>
        </h1>
        <div className="section-rule" />
        <p className="section-lede">
          Approve each handled event: approval emits the one resolution JSON (actions, contact log,
          tags, attachments, timing) to the Portal and files the batch into the dated station
          archive. Return anything incomplete to the operator's queue.
        </p>
      </header>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <StatusTabs tabs={REVIEW_TABS} active={tab} totals={data?.totals} onSelect={(k) => { setTab(k); setChecked(new Set()); }} />
        {awaiting && (
          <button
            type="button"
            className={stopsView ? 'btn btn-sm btn-primary' : 'btn btn-sm'}
            data-testid="stops-filter"
            aria-pressed={stopsView}
            title="Only the cases with a stop waiting for approval, oldest first"
            onClick={() => { setStopsView((v) => !v); setChecked(new Set()); }}
          >
            {STOPS_FILTER_LABEL(stopRows.length)}
          </button>
        )}
        <input
          className="text-input"
          type="search"
          aria-label="Search events for review"
          style={{ marginLeft: 'auto', maxWidth: 320 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search phone / email / name / member ID / text"
        />
      </div>

      {awaiting && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={toggleAll} disabled={!items.length}>
            {checked.size === items.length && items.length > 0 ? 'Clear selection' : 'Select all'}
          </button>
          <input
            className="text-input"
            aria-label="Batch resolution note"
            style={{ maxWidth: 320 }}
            value={batchNote}
            onChange={(e) => setBatchNote(e.target.value)}
            placeholder="Batch resolution note (optional to approve; required to archive)"
          />
          <button
            type="button"
            className="btn btn-sm btn-pill-approve"
            disabled={approving || checked.size === 0}
            onClick={() => void runApproveSelected()}
          >
            {STAGED_WORDING.approveButton(checked.size)}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={approving || archiving || checked.size === 0}
            title="End the checked items with NO resolution. Nothing is sent to the Portal; each becomes Handled - no action and can be reopened. The note is required."
            onClick={() => void runArchiveSelected()}
          >
            Archive without resolution ({checked.size})
          </button>
        </div>
      )}

      {banner && (
        <div className="note" style={{ marginBottom: 14 }}>
          {banner}
        </div>
      )}

      {/* Wave 3 item 4 (William, 2 Oct 2026: "all items go to approval then take effect"): the ticked
          cases carry presses that wait for this approval, and approving runs them. Said before the
          press, from the list rows' own counts; nothing shows when no ticked case carries one. */}
      {awaiting && tickedQueued.presses > 0 && (
        <div className="note warn" role="note" style={{ marginBottom: 14 }} data-testid="review-queued-note">
          {STAGED_WORDING.batchHeadsUp(tickedQueued.presses, tickedQueued.cases)}
        </div>
      )}

      {/* Not on the open-cases switch (William, 2 Oct 2026): the review decision (Approve / Return to
          queue) exists only in this side panel (reviewMode); the pop-out page has no review mode. */}
      <QueueTable
        grid={grid}
        selectedId={selected}
        loading={loading}
        onSelect={setSelected}
        emptyLabel={tab === 'cleared' ? 'Nothing cleared yet.' : 'Nothing awaiting review.'}
      />

      {selected && (
        <EventDrawer
          eventId={selected}
          hasAudioHint={items.find((r) => r.eventId === selected)?.hasAudio ?? false}
          onClose={() => setSelected(null)}
          onChanged={onDrawerChanged}
          reviewMode
        />
      )}
    </div>
  );
}
